import React, { useEffect, useMemo, useState } from 'react';
import styles from '../../styles/PreasignacionMateriasStyles/PreasignacionMaterias.module.css';

function SubjectCard({ 
  title, 
  icon, 
  subjects, 
  type, 
  emptyMessage = 'No hay materias en esta categoría',
  selectable = false,
  autoLocked = false,
  materiasSeleccionadas = [],
  materiasPreasignadas = [],
  onMateriaToggle,
  onSelectAll
}) {
  const PAGE_SIZE = 10;
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    setCurrentPage(1);
  }, [subjects, title]);

  // Convertir todos los IDs a números para comparación consistente
  const seleccionadasSet = new Set(materiasSeleccionadas.map(id => Number(id)));
  const preasignadasSet = new Set(materiasPreasignadas.map(id => Number(id)));

  const totalPages = Math.max(1, Math.ceil((subjects?.length || 0) / PAGE_SIZE));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const paginatedSubjects = useMemo(() => {
    if (!subjects || subjects.length === 0) return [];
    const startIndex = (safeCurrentPage - 1) * PAGE_SIZE;
    return subjects.slice(startIndex, startIndex + PAGE_SIZE);
  }, [subjects, safeCurrentPage]);
  
  const getCardClass = () => {
    switch (type) {
      case 'assigned':
        return styles.assignedCard;
      case 'pending':
        return styles.pendingCard;
      case 'optional':
        return styles.optionalCard;
      default:
        return styles.subjectCard;
    }
  };

  const getIconClass = () => {
    switch (type) {
      case 'assigned':
        return styles.assignedIcon;
      case 'pending':
        return styles.pendingIcon;
      case 'optional':
        return styles.optionalIcon;
      default:
        return styles.cardIcon;
    }
  };

  // Verificar si todas las materias de esta sección están seleccionadas
  const todasSeleccionadas = subjects.length > 0 && 
    subjects.every(s => seleccionadasSet.has(Number(s.id)) || preasignadasSet.has(Number(s.id)));

  // Manejar seleccionar/deseleccionar todas
  const handleSelectAllChange = () => {
    if (onSelectAll) {
      const seleccionables = subjects.filter(s => !preasignadasSet.has(Number(s.id)));
      onSelectAll(seleccionables, !todasSeleccionadas);
    }
  };

  return (
    <div className={`${styles.subjectCard} ${getCardClass()}`}>
      <div className={styles.cardHeader}>
        <div className={`${styles.cardIcon} ${getIconClass()}`}>
          {icon}
        </div>
        <h3 className={styles.cardTitle}>{title}</h3>
        {selectable && subjects.length > 0 && !autoLocked && (
          <label className={styles.selectAllLabel}>
            <input
              type="checkbox"
              checked={todasSeleccionadas}
              onChange={handleSelectAllChange}
              className={styles.selectAllCheckbox}
            />
            Seleccionar todas
          </label>
        )}
      </div>
      
      <div className={styles.cardContent}>
        {(!subjects || subjects.length === 0) ? (
          <p className={styles.noSubjects}>{emptyMessage}</p>
        ) : (
          <div className={styles.subjectsTable}>
            <div className={styles.subjectsHeader}>
              {selectable && <div className={styles.subjectHeaderCell} style={{ width: '40px' }}></div>}
              <div className={styles.subjectHeaderCell}>Materia</div>
              <div className={styles.subjectHeaderCell}>Créditos</div>
              <div className={styles.subjectHeaderCell}>Modalidad</div>
              <div className={styles.subjectHeaderCell}>Estado</div>
            </div>
            
            <div className={styles.subjectsBody}>
              {paginatedSubjects.map((subject) => {
                const subjectIdNum = Number(subject.id);
                const estaSeleccionada = seleccionadasSet.has(subjectIdNum);
                const yaPreasignada = preasignadasSet.has(subjectIdNum);
                // Las materias automáticas (recursadas / siguientes) quedan
                // bloqueadas: siempre marcadas y sin posibilidad de deselección.
                const locked = autoLocked || yaPreasignada;
                const checked = autoLocked ? true : (estaSeleccionada || yaPreasignada);
                
                return (
                  <div 
                    key={subject.id} 
                    className={`${styles.subjectRow} ${estaSeleccionada ? styles.subjectRowSelected : ''} ${yaPreasignada ? styles.subjectRowPreasignada : ''}`}
                    onClick={(e) => {
                      e.preventDefault();
                      if (!locked && onMateriaToggle) {
                        onMateriaToggle(subject.id);
                      }
                    }}
                    style={{ cursor: locked ? 'default' : 'pointer' }}
                  >
                    {selectable && (
                      <div className={styles.subjectCell} style={{ width: '40px' }}>
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={locked}
                          onChange={(e) => {
                            e.stopPropagation();
                            if (!locked && onMateriaToggle) {
                              onMateriaToggle(subject.id);
                            }
                          }}
                          className={styles.materiaCheckbox}
                        />
                      </div>
                    )}
                    <div className={styles.subjectCell}>
                      <div className={styles.subjectInfo}>
                        <div className={styles.subjectIcon}>
                          <svg viewBox="0 0 24 24" fill="currentColor">
                            <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
                          </svg>
                        </div>
                        <span className={styles.subjectName}>{subject.materia}</span>
                      </div>
                    </div>
                    <div className={styles.subjectCell}>
                      <span className={styles.credits}>{subject.creditos}</span>
                    </div>
                    <div className={styles.subjectCell}>
                      <span className={styles.modality}>{subject.modalidad}</span>
                    </div>
                    <div className={styles.subjectCell}>
                      {yaPreasignada ? (
                        <div className={styles.estadoPreasignada}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" className={styles.checkIcon}>
                            <polyline points="20,6 9,17 4,12"/>
                          </svg>
                          <span>Preasignada</span>
                        </div>
                      ) : autoLocked ? (
                        <div className={styles.estadoSeleccionada}>
                          <span>Automática</span>
                        </div>
                      ) : estaSeleccionada ? (
                        <div className={styles.estadoSeleccionada}>
                          <span>Seleccionada</span>
                        </div>
                      ) : (
                        <div className={styles.estadoPendiente}>
                          <span>Disponible</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {subjects.length > PAGE_SIZE && (
              <div className={styles.paginationControls}>
                <button
                  type="button"
                  className={styles.paginationButton}
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={safeCurrentPage === 1}
                >
                  Anterior
                </button>
                <span className={styles.paginationInfo}>
                  Pagina {safeCurrentPage} de {totalPages} ({subjects.length} materias)
                </span>
                <button
                  type="button"
                  className={styles.paginationButton}
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={safeCurrentPage === totalPages}
                >
                  Siguiente
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default SubjectCard;
